/** @format */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    CircularProgress,
    Typography,
    Alert,
    Button,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import { fetchSandboxCircleByToken } from "../../redux/actions/sandboxAction";
import {
    selectSandboxCircle,
    selectSandboxIsLoading,
    selectSandboxError,
} from "../../redux/selectors/sandboxSelector";
import { clearSandboxState } from "../../redux/slices/sandboxSlice";
import { colors } from "../../utility/types/constant";
import {
    SandboxPageWrapper,
    SandboxHeroBanner,
    SandboxBadge,
    SandboxHeroTitle,
    SandboxHeroSubtitle,
    SandboxContentArea,
} from "./styles";
import SandboxDashboard from "./SandboxDashboard";

const SandboxSharedPage: React.FC = () => {
    const { shareToken } = useParams<{ shareToken: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { t } = useTranslation("sandbox");

    const circle = useSelector(selectSandboxCircle);
    const isLoading = useSelector(selectSandboxIsLoading);
    const error = useSelector(selectSandboxError);

    useEffect(() => {
        if (shareToken) {
            // Clear any previously loaded circle, then fetch by token
            dispatch(clearSandboxState());
            dispatch(fetchSandboxCircleByToken(shareToken));
        }
    }, [dispatch, shareToken]);

    return (
        <SandboxPageWrapper>
            <SandboxHeroBanner>
                <SandboxBadge label={t("shared.badge")} />
                <SandboxHeroTitle>
                    {circle ? circle.circleName : t("shared.heroTitleFallback")}
                </SandboxHeroTitle>
                <SandboxHeroSubtitle>
                    {t("shared.heroSubtitle")}
                </SandboxHeroSubtitle>
            </SandboxHeroBanner>

            <SandboxContentArea>
                {isLoading && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "60px 0",
                            gap: "16px",
                        }}>
                        <CircularProgress sx={{ color: colors.mainSecondary }} />
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.95rem",
                                color: "#6c757d",
                            }}>
                            {t("shared.loading")}
                        </Typography>
                    </Box>
                )}

                {error && !isLoading && (
                    <Box sx={{ textAlign: "center", padding: "60px 0" }}>
                        <Alert
                            severity="warning"
                            sx={{
                                marginBottom: "28px",
                                fontFamily: "'Montserrat', sans-serif",
                                maxWidth: "500px",
                                margin: "0 auto 28px",
                            }}>
                            {error}
                        </Alert>
                        <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            onClick={() => navigate("/sandbox/demo")}
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                padding: "12px 28px",
                                borderRadius: "50px",
                                backgroundColor: colors.mainSecondary,
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#17a37d" },
                            }}>
                            {t("shared.createOwn")}
                        </Button>
                    </Box>
                )}

                {circle && !isLoading && (
                    <SandboxDashboard isReadOnly={true} />
                )}
            </SandboxContentArea>
        </SandboxPageWrapper>
    );
};

export default SandboxSharedPage;
